/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { InviteParentSchema, type InviteParentInput } from '@therapy/shared-validators';
import { Check, Copy, Send } from 'lucide-react';
import { useCallback, useState, type SyntheticEvent, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type Client, type ParentInvite } from '../../lib/api/resources.js';
import { useInviteParent } from '../../lib/queries/clinical.js';

interface InviteParentFormProps {
  readonly client: Client;
  readonly onDone: () => void;
}

/** Shows the one-time activation link after a successful invite. */
export function InviteResult({
  invite,
  onDone,
}: {
  invite: ParentInvite;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    void (async (): Promise<void> => {
      await navigator.clipboard.writeText(invite.activationUrl);
      setCopied(true);
    })();
  }, [invite.activationUrl]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('admin.invite.success', { email: invite.email })}
      </p>
      <div>
        <label
          htmlFor="invite-link"
          className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {t('admin.invite.linkLabel')}
        </label>
        <div className="flex gap-2">
          <input
            id="invite-link"
            readOnly
            value={invite.activationUrl}
            className="focus-ring flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
          <Button type="button" variant="outline" onClick={copy}>
            {copied ? (
              <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
            {copied ? t('admin.invite.copied') : t('admin.invite.copy')}
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-400">{t('admin.invite.note')}</p>
      <div className="flex justify-end">
        <Button type="button" onClick={onDone}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

/** Invite-a-parent form (Admin → Clients). Creates an invited PARENT login. */
export function InviteParentForm({ client, onDone }: InviteParentFormProps): JSX.Element {
  const { t } = useTranslation();
  const invite = useInviteParent();
  const [result, setResult] = useState<ParentInvite | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InviteParentInput>({
    resolver: zodResolver(InviteParentSchema),
    defaultValues: { fullName: '', email: client.email ?? '' },
  });

  const submit = useCallback(
    async (values: InviteParentInput): Promise<void> => {
      const data = await invite.mutateAsync({ id: client.id, input: values });
      setResult(data);
    },
    [invite, client.id],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  if (result !== null) {
    return <InviteResult invite={result} onDone={onDone} />;
  }

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('admin.invite.intro', { child: client.fullName })}
      </p>
      <FormField
        id="invite-name"
        type="text"
        label={t('admin.invite.parentName')}
        autoComplete="name"
        error={errors.fullName?.message}
        registration={register('fullName')}
      />
      <FormField
        id="invite-email"
        type="email"
        label={t('admin.invite.email')}
        autoComplete="email"
        error={errors.email?.message}
        registration={register('email')}
      />
      {invite.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('admin.invite.error')}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || invite.isPending}>
          <Send className="h-4 w-4" aria-hidden="true" />
          {t('admin.invite.submit')}
        </Button>
      </div>
    </form>
  );
}
